import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiPlay, FiPause } from 'react-icons/fi';

/**
 * Confirmation for operating the pipeline. Live runs require typing LIVE.
 * kind: 'dry_run' | 'live' | 'pause' | 'resume'
 * dag:  'planner' (guest_sms_daily) | 'dispatcher' (guest_sms_dispatch)
 */
const COPY = {
  planner: {
    dry_run: {
      title: 'Plan today’s texts now?',
      body: 'Exports arrivals for the next week, cleans and dedupes them, and writes each guest’s send time to the queue (see Upcoming). Nothing is sent by this step — the dispatcher sends when each slot is due.',
      cta: 'Start planner',
      tone: 'bg-zinc-900 hover:bg-zinc-800',
      icon: FiPlay,
    },
    live: {
      title: 'Run the legacy immediate send?',
      body: 'This bypasses the planner and texts every eligible guest in the window right now. Only use this for rollback. Type LIVE to confirm.',
      cta: 'Send live',
      tone: 'bg-rose-600 hover:bg-rose-700',
      icon: FiAlertTriangle,
      confirmWord: 'LIVE',
    },
    pause: {
      title: 'Pause the daily planner?',
      body: 'No new texts will be planned until resumed. Texts already in the queue will still be sent by the dispatcher — pause the dispatcher too for a full stop.',
      cta: 'Pause planner',
      tone: 'bg-amber-600 hover:bg-amber-700',
      icon: FiPause,
    },
    resume: {
      title: 'Resume the daily planner?',
      body: 'Airflow may immediately create a catch-up run for the most recent missed 4 AM slot.',
      cta: 'Resume planner',
      tone: 'bg-emerald-600 hover:bg-emerald-700',
      icon: FiPlay,
    },
  },
  dispatcher: {
    dry_run: {
      title: 'Dispatch due texts now (dry run)?',
      body: 'Processes every queued text whose slot has passed as a simulation. No SMS leaves Vonage; the queue items are marked “sent (dry run)” and re-planned tomorrow.',
      cta: 'Start dry dispatch',
      tone: 'bg-zinc-900 hover:bg-zinc-800',
      icon: FiPlay,
    },
    live: {
      title: 'Send every due text now?',
      body: 'Every queued text whose slot has passed will be sent to the guest immediately, regardless of the server’s dry-run setting. Type LIVE to confirm.',
      cta: 'Send live',
      tone: 'bg-rose-600 hover:bg-rose-700',
      icon: FiAlertTriangle,
      confirmWord: 'LIVE',
    },
    pause: {
      title: 'Pause the dispatcher?',
      body: 'Nothing will be sent while paused — this is the emergency stop. Queued texts stay in the queue and go out once resumed (late ones are sent on the next tick).',
      cta: 'Pause dispatcher',
      tone: 'bg-amber-600 hover:bg-amber-700',
      icon: FiPause,
    },
    resume: {
      title: 'Resume the dispatcher?',
      body: 'Everything that became due while paused will be sent on the next 15-minute tick.',
      cta: 'Resume dispatcher',
      tone: 'bg-emerald-600 hover:bg-emerald-700',
      icon: FiPlay,
    },
  },
};

export default function ConfirmRunModal({ kind, dag = 'planner', busy = false, error = '', onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  useEffect(() => setTyped(''), [kind, dag]);

  if (!kind) return null;
  const copy = (COPY[dag] || COPY.planner)[kind];
  if (!copy) return null;
  const Icon = copy.icon;
  const needsWord = Boolean(copy.confirmWord);
  const canConfirm = !busy && (!needsWord || typed.trim() === copy.confirmWord);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={busy ? undefined : onCancel}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-confirm-title"
        className="mx-4 w-full max-w-md rounded-[28px] bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${kind === 'live' ? 'bg-rose-50 text-rose-600' : 'bg-zinc-100 text-zinc-700'}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 id="sms-confirm-title" className="text-lg font-semibold text-zinc-900">
              {copy.title}
            </h3>
            <p className="mt-1.5 text-sm text-zinc-500">{copy.body}</p>
          </div>
        </div>

        {needsWord ? (
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`Type ${copy.confirmWord}`}
            className="mt-4 h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm uppercase tracking-widest outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
          />
        ) : null}

        {error ? <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${copy.tone}`}
          >
            {busy ? 'Working…' : copy.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
